import React from 'react'
import pluralize from 'pluralize'
import { useSelector } from 'react-redux'
import { IconAdjustmentsHorizontal, IconChartAreaLine, IconFilterOff } from '@tabler/icons-react'

import NoData from '@/components/NoData'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'

import { describeTrendFilters } from '../trendsFilters.helpers'

/**
 * Why a trend has nothing to plot, and what it should say about it.
 *
 * `noMatchingCalls` — nothing was sampled in this scope at all.
 * `allIrrelevant` — calls were analyzed, none of them related to the trend. Worth
 * distinguishing: the board did find calls, so "no data" alone reads as though
 * the filters matched nothing, and the count is the useful part.
 */
const REASONS = {
  noMatchingCalls: {
    header: 'No data for the selected filters',
    message: ({ trendName }) =>
      `Balto sampled no calls for “${trendName}” in this scope. Widen the date range or remove a filter to bring this metric back.`,
  },
  allIrrelevant: {
    header: 'No calls related to this trend',
    message: ({ trendName, callCount, callType }) =>
      `Balto analyzed ${callCount} ${pluralize(callType, callCount)} in this scope, but none of them related to “${trendName}”. Widen the date range or remove a filter to take in calls that might.`,
  },
}

/**
 * Shown when a tracked insight has nothing to plot inside the current board scope.
 *
 * This is an expected outcome, not a failure. An insight only ever has data for
 * the calls it was generated from, so a board filter that doesn't overlap that
 * set legitimately leaves nothing to draw — which is why the applied scope is
 * spelled out and both ways back are offered, rather than showing an empty
 * chart or an error.
 *
 * @param {object} reason - { kind, callCount, callType }; see REASONS
 * @param {string} trendName - the insight's name, quoted in the message
 * @param {string} dateRangeLabel - the board's date range, always part of the scope
 * @param {Function} onAdjustFilters - opens the filter modal
 * @param {Function} onClearFilters - clears the board filters
 */
export const TrendNoDataState = ({
  reason,
  trendName,
  dateRangeLabel,
  onAdjustFilters,
  onClearFilters,
}) => {
  // the board shares the Call Explorer filter slice
  const { filters } = useSelector((state) => state.callSearch)
  const appliedFilters = describeTrendFilters(filters)
  const { header, message } = REASONS[reason.kind]

  return (
    <NoData
      data-testid="trend-no-data-for-filters"
      headerText={header}
      icon={<IconChartAreaLine className="icon-svg muted-text" />}
    >
      <div className="flex-column flex-center medium-gap">
        <p>{message({ trendName, ...reason })}</p>
        <div>
          <div className="muted-text small-text">APPLIED SCOPE</div>
          <div className="flex flex-center flex-wrap small-gap mt">
            <Pill small outlined>
              {`Date range: ${dateRangeLabel}`}
            </Pill>
            {appliedFilters.map((filter) => (
              <Pill small outlined key={filter.accessor}>
                {`${filter.label}: ${filter.value}`}
              </Pill>
            ))}
          </div>
        </div>
        <div className="flex flex-center small-gap mt">
          <Button
            brand
            icon={<IconAdjustmentsHorizontal />}
            content="Adjust filters"
            onClick={onAdjustFilters}
            data-testid="trend-adjust-filters-button"
          />
          <Button
            icon={<IconFilterOff />}
            content="Clear filters"
            onClick={onClearFilters}
            data-testid="trend-clear-filters-button"
          />
        </div>
      </div>
    </NoData>
  )
}
